// Huddle — the daily 10:00 stand-up. Replaces the printed form each person fills in and
// reads aloud. Migration 184; backend src/modules/huddle/huddle.routes.js.
//
// THE LAYOUT IS THE PAPER FORM (ruling 2). Four rows, in the printed order, with the
// bilingual labels kept EXACTLY as printed. They are not renamed to English — the team
// reads the Hindi, and a screen that drops it is a screen that stops matching the form
// people already know.
//
// VISIBILITY (ruling 8): admin + management see everyone; everyone else sees only
// themselves. Enforced on the SERVER — this file simply does not render what it won't get.
//
// PRINTING (ruling 11) is done with @media print CSS rather than a server-side PDF: the
// labels are Devanagari, the repo ships no font files, and pdfkit would need a bundled
// Devanagari TTF to render पॉजिटिव+कल at all — where the browser already does it correctly.
// The same stylesheet gives the phone layout for free.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';
import HuddleGrid from '../components/HuddleGrid';
import { useAuth } from '../context/AuthContext';
import { reportError } from '../utils/reportError';

const C = { navy: '#3B3A8F', red: '#E22D2D', green: '#166534', amber: '#B45309',
  text: '#1a1a2e', muted: '#888', border: '#e8e8e8', bg: '#F7F6F3' };

// The four printed rows. Label text is verbatim from the paper form — do not translate,
// do not shorten, do not reorder.
const L = {
  header: ['Department', 'Date', 'Name'],
  positive: 'POSITIVE +VE YESTERDAY / पॉजिटिव+कल',
  stuck: 'STUCK/PENDING/UPCOMING / अटका हुआ/लंबित/आने वाला',
  target: 'TODAY TARGET / आज का लक्ष्य',
};

const input = { padding: '7px 9px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, fontFamily: 'inherit' };
const btn = { padding: '7px 13px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600 };
const btnP = { ...btn, background: C.navy, color: '#fff' };
const fmtD = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }) : '—';

// ─── Print stylesheet ────────────────────────────────────────────────────────
// Page 1 = the form (matches the paper). Page 2 = the affirmations panel, i.e. the REVERSE
// of the sheet when duplex-printed. Everything else on the app is hidden.
const PRINT_CSS = `
@media screen { .hd-print { max-width: 820px; margin: 0 auto; } }
@media print {
  @page { size: A4 portrait; margin: 12mm; }
  /* The app shell reserves a full viewport — the sidebar is height:100vh and the layout
     wrappers are min-height:100vh. visibility:hidden hides their INK but keeps their BOX,
     so those viewport-sized boxes were still being paginated and emitting trailing blank
     pages after the sheet. Collapse the boxes, do not just blank them. */
  html, body { height: auto !important; overflow: visible !important; }
  aside { display: none !important; }
  [style*="100vh"] { height: auto !important; min-height: 0 !important; }
  body * { visibility: hidden; }
  .hd-print, .hd-print * { visibility: visible; }
  /* position:absolute took the sheet OUT OF FLOW, and out-of-flow content does not
     paginate — which is what pushed the affirmations panel onto a third page rather than
     letting it break cleanly. It stays in flow now. */
  .hd-print { position: static !important; width: 100% !important; }
  .hd-noprint { display: none !important; }
  .hd-page-1 { page-break-inside: avoid; }
  .hd-page-2 { page-break-before: always; }
  /* 2,234 characters at 15px/1.9 in a ~659px column is ~780px of text before the heading
     and padding, which does not reliably clear one A4 page. Print sizing is set here so the
     on-screen panel keeps its comfortable reading size. */
  .hd-affirm { font-size: 11.5px !important; line-height: 1.6 !important; }
  .hd-affirm-box { min-height: 0 !important; padding: 16px !important; }
  .hd-box { border: 1.5px solid #000 !important; }
}
/* Phone: the same markup has to read cleanly at 360px, so the header row stacks. */
@media (max-width: 560px) {
  .hd-head { grid-template-columns: 1fr !important; }
  .hd-head > div { border-right: none !important; border-bottom: 1px solid #000; }
  .hd-head > div:last-child { border-bottom: none; }
}
`;

export default function Huddle() {
  const { user } = useAuth();
  const [tab, setTab] = useState('sheet');
  const canSeeAll = ['admin', 'management'].includes(user?.role);
  return (
    <div style={{ padding: 20, maxWidth: 1180, margin: '0 auto', color: C.text }}>
      <style>{PRINT_CSS}</style>
      <div className="hd-noprint" style={{ marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 22 }}>🗣️ Huddle</h1>
        <div style={{ color: C.muted, fontSize: 12, marginTop: 3 }}>
          Daily 10:05 · 15 minutes · one sheet per person. Meeting attendance only — this is not factory attendance.
        </div>
      </div>
      <div className="hd-noprint" style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {[['sheet', '📝 My Sheet'], ['day', '🗓 My Day'], ['facilitator', '📽 Facilitator'], ['print', '🖨 Print'],
          ...(canSeeAll ? [['config', '⚙️ Affirmations']] : []),
          ...(user?.role === 'admin' ? [['roster', '👥 Roster']] : []),
          ...(canSeeAll ? [['mgmt', '📊 Management']] : [])].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} style={{ ...btn, border: '1px solid ' + (tab === k ? C.navy : '#ddd'),
            background: tab === k ? C.navy : '#fff', color: tab === k ? '#fff' : '#666' }}>{l}</button>
        ))}
      </div>
      {tab === 'sheet' && <MySheet />}
      {/* A SECOND PANE, not a fourth stripe on the sheet. The sheet is what you fill in before
          10:03; the grid is what you work against for the rest of the day — which is exactly
          why its lock differs (writableEntry { exempt: true }). */}
      {tab === 'day' && <MyDay />}
      {tab === 'facilitator' && <Facilitator canSeeAll={canSeeAll} />}
      {tab === 'print' && <PrintView />}
      {tab === 'config' && <ConfigTab />}
      {tab === 'roster' && user?.role === 'admin' && <RosterAdmin />}
      {tab === 'mgmt' && canSeeAll && <ManagementDashboard />}
    </div>
  );
}

// GPO_MAX matches huddle_targets.gpo_no / huddle_blockers.gpo_no — both varchar(20). Past
// SEARCH_MAX the text cannot plausibly be a GPO number, a style or a colour: 329 of one
// day's 389 gpo-search requests were over 20 characters, because someone typed a whole
// 260-character huddle update into this 130px box and every keystroke came back 200 with
// an empty dropdown, which told them nothing was wrong.
const GPO_MAX = 20;
const SEARCH_MAX = 20;
// Prose, not an identifier: four or more words. Deliberately NOT three — a real colour
// search is "MAUVE 19-1716 TPG", three words and 17 characters, and blocking that would
// break a legitimate lookup to catch a case the 20-character cap already catches.
const looksLikeProse = (s) => String(s || '').trim().split(/\s+/).length >= 4;

// ─── GPO + free text, together on one line (ruling 4) ────────────────────────
// The team already writes "1788 Beige Twill". Picking GPO 1788 brings its style/colour so
// they type only what is actually stuck. NEITHER half is required on its own and the field
// is never GPO-only — free text alone is always valid.
function LineInput({ value, gpo, onChange, placeholder, onSubmit, autoFocus, after }) {
  const [q, setQ] = useState(gpo || '');
  const [hits, setHits] = useState([]);
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(null);
  const [searching, setSearching] = useState(false);
  useEffect(() => { setQ(gpo || ''); }, [gpo]);
  useEffect(() => {
    const t0 = q.trim();
    // Stop searching once the text cannot plausibly be an identifier. The request is
    // skipped — but the dropdown is NOT left blank, which is the whole point (see below).
    if (!open || t0.length < 1 || t0.length > SEARCH_MAX || looksLikeProse(t0)) { setHits([]); setSearching(false); return; }
    let dead = false;
    setSearching(true);
    const t = setTimeout(async () => {
      const r = await api.get('/huddle/gpo-search?q=' + encodeURIComponent(t0)).catch(() => null);
      if (!dead) { setHits(r ? (r.data.data || []) : []); setSearching(false); }
    }, 200);
    return () => { dead = true; clearTimeout(t); };
  }, [q, open]);
  const pick = (g) => {
    setPicked(g); setQ(g.gpo_no); setOpen(false); setHits([]);
    // The pick brings style/colour; it does NOT overwrite what the person typed.
    const brought = [g.style_no, g.colour].filter(Boolean).join(' ');
    onChange({ gpo_no: g.gpo_no, body: value ? value : brought });
  };
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <div style={{ position: 'relative', flex: '0 0 130px' }}>
        <input style={{ ...input, width: '100%', fontFamily: 'monospace' }} placeholder="GPO no."
          maxLength={GPO_MAX} value={q}
          onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 180)}
          onChange={e => { setQ(e.target.value); setOpen(true); onChange({ gpo_no: e.target.value.trim() || null, body: value }); }} />
        {open && q.trim().length > 0 && (
          <div style={{ position: 'absolute', zIndex: 30, top: '100%', left: 0, minWidth: 300, background: '#fff',
            border: '1px solid #ddd', borderRadius: 7, boxShadow: '0 6px 20px rgba(0,0,0,.12)', maxHeight: 230, overflowY: 'auto' }}>
            {hits.length > 0 ? hits.map(g => (
              <div key={g.gpo_no} onMouseDown={() => pick(g)}
                style={{ padding: '7px 10px', cursor: 'pointer', fontSize: 12, borderBottom: '1px solid #f5f5f5' }}>
                <strong style={{ fontFamily: 'monospace', color: C.red }}>{g.gpo_no}</strong>
                <span style={{ color: C.muted }}> · {[g.style_no, g.colour].filter(Boolean).join(' · ') || g.item_desc || '—'}</span>
              </div>
            )) : searching ? (
              <div style={{ padding: '9px 11px', fontSize: 12, color: C.muted }}>Searching…</div>
            ) : looksLikeProse(q) || q.trim().length >= SEARCH_MAX ? (
              // THE FIX. An empty dropdown is what let someone type 260 characters in here
              // without ever suspecting they were in the wrong field. Say which box this is.
              <div style={{ padding: '10px 12px', fontSize: 12, color: C.red, background: '#FCEBEB', lineHeight: 1.5 }}>
                <strong>This is the GPO number box.</strong><br />
                Type your update in the field on the right. →
              </div>
            ) : (
              <div style={{ padding: '9px 11px', fontSize: 12, color: C.muted }}>No GPO matches “{q.trim()}”.</div>
            )}
          </div>
        )}
      </div>
      <input style={{ ...input, flex: '1 1 220px' }} placeholder={placeholder} value={value || ''} autoFocus={autoFocus}
        onChange={e => onChange({ gpo_no: q.trim() || null, body: e.target.value })}
        onKeyDown={e => { if (e.key === 'Enter' && onSubmit) onSubmit(); }} />
      {picked && <span style={{ fontSize: 10, color: C.muted, alignSelf: 'center' }}>
        {[picked.style_no, picked.colour].filter(Boolean).join(' · ')}</span>}
      {/* Slot for a control that belongs to the line being WRITTEN — today just the
          waiting-on picker on the Stuck line. Passed in rather than built here, because
          LineInput is shared by all three sections and only one of them can wait on
          somebody. */}
      {after}
    </div>
  );
}

function CarryBadge({ n }) {
  if (!n) return null;
  return <span title={`This item has been carried forward ${n} time${n === 1 ? '' : 's'}`}
    style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: n >= 3 ? C.red : C.amber,
      background: n >= 3 ? '#FCEBEB' : '#FEF3E2', borderRadius: 10, padding: '1px 7px', whiteSpace: 'nowrap' }}>
    carried {n}×</span>;
}

// THE LINKED TASK, LIVE (mig 249). The row used to print "→ TASK-2026-034" and stop, which
// says only that a task exists somewhere — it could have been finished in June or never
// started. Everything here is read through huddle_blockers.promoted_task_id at request time,
// so the chip reports the task as it is now.
//
// This is also what makes the resolve refusal fair. A hidden button with no explanation is a
// wall; a hidden button beside "pending · 0% · 4 days overdue" explains itself.
function TaskChip({ it }) {
  if (!it.task_no) return null;
  const late = it.task_overdue_days > 0;
  const words = { pending: 'pending', in_progress: 'in progress', sent_back: 'sent back',
                  completed: 'completed', audited: 'audited', cancelled: 'cancelled' };
  return (
    <span title={it.promoted_at ? 'Promoted from this item' : 'This item was accepted from this task'}
      style={{ marginLeft: 6, fontSize: 10, whiteSpace: 'nowrap', borderRadius: 10, padding: '1px 7px',
        color: late ? C.red : C.navy, background: late ? '#FCEBEB' : '#EEF0FA' }}>
      → {it.task_no}{it.task_owner_name ? ` · ${it.task_owner_name}` : ''}
      {it.task_status ? ` · ${words[it.task_status] || it.task_status}` : ''}
      {it.task_open ? ` · ${it.task_progress_pct || 0}%` : ''}
      {late ? ` · ${it.task_overdue_days}d overdue` : ''}
    </span>
  );
}

// ─── MY SHEET — the paper form, in order ─────────────────────────────────────
// A draft is what you have typed but not yet pressed + Add on. It used to live ONLY in
// component state: no autosave, no beforeunload, no localStorage — so a reload, a stray
// navigation, or pressing Submit expecting it to save everything all silently threw the
// text away. 13 of 16 sheets were empty on day one. Both halves of the fix are here:
// (a) the draft is persisted as it is typed, and (b) Submit commits it.
const draftKey = (entryId, kind) => `huddle:draft:${entryId}:${kind}`;
const isEmptyDraft = (dr) => !dr || (!String(dr.body || '').trim() && !String(dr.gpo_no || '').trim());
const readDraft = (entryId, kind) => {
  try {
    const raw = window.localStorage.getItem(draftKey(entryId, kind));
    if (!raw) return null;
    const v = JSON.parse(raw);
    return isEmptyDraft(v) ? null : v;
  } catch (_) { return null; }
};

function MySheet() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [sug, setSug] = useState({ personal: [], role_queues: [] });
  const [draft, setDraft] = useState({ body: '', gpo_no: null, owed_by_user_id: null });
  const [draftT, setDraftT] = useState({ body: '', gpo_no: null });
  const [draftP, setDraftP] = useState({ body: '', gpo_no: null });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [restored, setRestored] = useState(false);
  // ROSTER, FETCHED ONCE FOR THE WHOLE SHEET. It was fetched lazily inside every ItemRow on
  // first click, so a sheet with six stuck items could issue six identical requests. The
  // compose line needs it before any row is clicked, which makes one shared fetch the
  // simpler answer as well as the cheaper one.
  const [roster, setRoster] = useState(null);   // null = not loaded yet
  useEffect(() => { let dead = false;
    api.get('/huddle/roster').then(r => { if (!dead) setRoster(r.data.data || []); }).catch(() => { if (!dead) setRoster([]); });
    return () => { dead = true; };
  }, []);

  const load = useCallback(async () => {
    const r = await api.get('/huddle/today').catch(e => { setErr(e.response?.data?.error || 'Failed to load'); return null; });
    if (r) setD(r.data.data);
  }, []);
  useEffect(() => { load(); api.get('/huddle/suggestions').then(r => setSug(r.data.data)).catch(() => {}); }, [load]);

  const entryId = d?.entry?.id || null;

  // (a) RESTORE — anything typed and not added is still here after a reload.
  useEffect(() => {
    if (!entryId || restored) return;
    const b = readDraft(entryId, 'blocker'), t = readDraft(entryId, 'target'), pz = readDraft(entryId, 'positive');
    if (b) setDraft(b);
    if (t) setDraftT(t);
    if (pz) setDraftP(pz);
    setRestored(true);
  }, [entryId, restored]);

  // (a) AUTOSAVE — debounced, so it survives a reload or navigating away mid-sentence.
  useEffect(() => {
    if (!entryId || !restored) return;
    const h = setTimeout(() => {
      try {
        if (isEmptyDraft(draft)) window.localStorage.removeItem(draftKey(entryId, 'blocker'));
        else window.localStorage.setItem(draftKey(entryId, 'blocker'), JSON.stringify(draft));
      } catch (_) { /* private mode / quota — the in-memory draft still works */ }
    }, 400);
    return () => clearTimeout(h);
  }, [draft, entryId, restored]);
  useEffect(() => {
    if (!entryId || !restored) return;
    const h = setTimeout(() => {
      try {
        if (isEmptyDraft(draftT)) window.localStorage.removeItem(draftKey(entryId, 'target'));
        else window.localStorage.setItem(draftKey(entryId, 'target'), JSON.stringify(draftT));
      } catch (_) { /* as above */ }
    }, 400);
    return () => clearTimeout(h);
  }, [draftT, entryId, restored]);
  // Same autosave for the positive draft — all three sections behave identically.
  useEffect(() => {
    if (!entryId || !restored) return;
    const h = setTimeout(() => {
      try {
        if (isEmptyDraft(draftP)) window.localStorage.removeItem(draftKey(entryId, 'positive'));
        else window.localStorage.setItem(draftKey(entryId, 'positive'), JSON.stringify(draftP));
      } catch (_) { /* private mode / quota — the in-memory draft still works */ }
    }, 400);
    return () => clearTimeout(h);
  }, [draftP, entryId, restored]);


  // Closing the tab with uncommitted text gets the browser's own warning. localStorage
  // already preserves it; this stops someone walking away believing it was saved.
  useEffect(() => {
    const pending = !isEmptyDraft(draft) || !isEmptyDraft(draftT);
    if (!pending) return;
    const onBeforeUnload = (ev) => { ev.preventDefault(); ev.returnValue = ''; return ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [draft, draftT]);

  if (err) return <div style={{ color: C.red }}>{err}</div>;
  if (!d) return <div style={{ color: C.muted }}>Loading…</div>;
  // NO SHEET IS A STATE, NOT A CRASH. MyDay has guarded this since it was written (:547);
  // MySheet never did, and it renders first — so `const e = d.entry` was null and the very
  // next read, e.department in the header, threw. The whole Huddle page died with
  // "Cannot read properties of null (reading 'department')" behind an error boundary.
  //
  // This is NOT the "hasn't opened it yet" case: ensureDay creates a sheet for the ENTIRE
  // roster the first time anyone touches the day, so a roster member always has one. The
  // people without a sheet are the ones deliberately EXCLUDED from the roster — today that
  // is both admin accounts, management, gate and maintenance_incharge. They were shown a
  // crash every time they opened the page, permanently, rather than being told they are not
  // on the huddle. Worded to say which it is, since "no sheet today" would read as a fault.
  if (!d.entry) return (
    <div style={{ color: C.muted, fontSize: 13 }}>
      You are not on the huddle roster, so you have no sheet today.
      {' '}Other people’s sheets are on the Facilitator tab.
    </div>
  );
  const locked = d.phase === 'locked' || d.phase === 'closed';
  const e = d.entry;

  const clearDraft = (kind) => { try { window.localStorage.removeItem(draftKey(e.id, kind)); } catch (_) { /* */ } };
  const call = async (fn) => { setBusy(true); setErr(''); try { await fn(); await load(); } catch (ex) { setErr(ex.response?.data?.error || 'Failed'); } finally { setBusy(false); } };
  const save = (patch) => call(() => api.patch(`/huddle/entry/${e.id}`, patch));
  const addBlocker = (b) => call(async () => { await api.post(`/huddle/entry/${e.id}/blockers`, b); setDraft({ body: '', gpo_no: null, owed_by_user_id: null }); clearDraft('blocker'); });
  const addTarget = (b) => call(async () => { await api.post(`/huddle/entry/${e.id}/targets`, b); setDraftT({ body: '', gpo_no: null }); clearDraft('target'); });
  const addPositive = (b) => call(async () => { await api.post(`/huddle/entry/${e.id}/positives`, b); setDraftP({ body: '', gpo_no: null }); clearDraft('positive'); });

  // (b) SUBMIT COMMITS THE DRAFT, and never silently drops it. Autosave alone would not
  // have helped the person who typed a blocker and pressed Submit expecting that to save
  // everything — so Submit adds what is pending, after telling them it is about to.
  const submitSheet = async () => {
    const pending = [];
    if (!isEmptyDraft(draftP)) pending.push(['positive', draftP, 'positives']);
    if (!isEmptyDraft(draft)) pending.push(['stuck', draft, 'blockers']);
    if (!isEmptyDraft(draftT)) pending.push(['target', draftT, 'targets']);
    if (pending.length) {
      const list = pending.map(([label, dr]) =>
        `  • ${label}: ${[dr.gpo_no, dr.body].filter(Boolean).join(' — ')}`).join('\n');
      if (!window.confirm(
        `You have text that has not been added to your sheet yet:\n\n${list}\n\n`
        + `Add ${pending.length === 1 ? 'it' : 'them'} and submit?\n\n`
        + `(Cancel to go back — nothing will be lost either way.)`)) return;
    }
    setBusy(true); setErr('');
    try {
      for (const [, dr, path] of pending) {
        await api.post(`/huddle/entry/${e.id}/${path}`, dr);
      }
      if (!isEmptyDraft(draftP)) { setDraftP({ body: '', gpo_no: null }); clearDraft('positive'); }
      // owed_by_user_id cleared with the rest: leaving a name selected after the line it
      // belonged to has been added would attach it to whatever is typed next.
      if (!isEmptyDraft(draft)) { setDraft({ body: '', gpo_no: null, owed_by_user_id: null }); clearDraft('blocker'); }
      if (!isEmptyDraft(draftT)) { setDraftT({ body: '', gpo_no: null }); clearDraft('target'); }
      await api.patch(`/huddle/entry/${e.id}`, { submit: true });
      await load();
    } catch (ex) {
      // The draft is deliberately NOT cleared on failure — it stays on screen and in
      // localStorage so a rejected line can be corrected rather than retyped.
      setErr(ex.response?.data?.error || 'Failed to submit');
    } finally { setBusy(false); }
  };

  return (
    <div>
      <PhaseBar d={d} isAdmin={user?.role === 'admin'} onChanged={load} />

      {(d.owed_to_me || []).length > 0 && (
        <Box title={`Owed by you — ${d.owed_to_me.length} item${d.owed_to_me.length === 1 ? '' : 's'}`} tone="amber">
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>
            Someone else is waiting on you. Clearing it does not close it — the person who raised it confirms.
          </div>
          {d.owed_to_me.map(o => <OwedRow key={o.id} it={o} onDone={load} setErr={setErr} />)}
        </Box>
      )}

      {/* RULING 3 — yesterday's target returns AT THE TOP. */}
      {/* A reopened day is stated at the top of the sheet. No auto-expiry means the only thing
          stopping an override becoming permanent is that everyone can see it. */}
      {d.day && d.day.status === 'reopened' && (
        <div style={{ background: '#FEF3C7', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 12, color: '#8A5A00' }}>
          <strong>Reopened</strong>{d.day.reopened_by_name ? ` by ${d.day.reopened_by_name}` : ''}
          {d.day.reopened_at ? ` at ${new Date(d.day.reopened_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })}` : ''}
          {' — still open.'} Anything written now is marked as written after the lock.
        </div>
      )}
      {d.carry_in.length > 0 && (
        <Box title={`Yesterday's targets — ${fmtD(d.carry_in[0].from_date)}`} tone="amber">
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 8 }}>
            Mark each one. <strong>Not done moves straight into today's stuck list</strong>, carrying its text — a reason is required.
          </div>
          {d.carry_in.map(t => <CarryRow key={t.id} t={t} locked={locked} onDone={load} setErr={setErr} />)}
        </Box>
      )}

      {/* THE FORM — four rows, printed order, printed labels. */}
      <div className="hd-box" style={{ border: '1.5px solid #333', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
        <div className="hd-head" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', borderBottom: '1.5px solid #333' }}>
          <HeadCell label={L.header[0]}>
            <input style={{ ...input, border: 'none', width: '100%', padding: 0, fontSize: 14, fontWeight: 700 }}
              value={e.department || ''} disabled={locked}
              onChange={ev => setD({ ...d, entry: { ...e, department: ev.target.value } })}
              onBlur={ev => save({ department: ev.target.value })} />
          </HeadCell>
          <HeadCell label={L.header[1]} border><strong style={{ fontSize: 14 }}>{fmtD(d.date)}</strong></HeadCell>
          <HeadCell label={L.header[2]}><strong style={{ fontSize: 14 }}>{e.name}</strong></HeadCell>
        </div>

        {/* Positive is now a LIST, like stuck and target (mig 207). It was the only section
            that could not be pointed at an order, counted, or carried — and the least filled
            of the three at 30%. Legacy blobs appear here as a single item, labelled. */}
        <Row label={L.positive}>
          {d.positives.map(pz => <ItemRow key={pz.id} it={pz} kind="positives" locked={locked} onDone={load} setErr={setErr} />)}
          {!locked && (
            <div style={{ marginTop: 6 }}>
              <LineInput value={draftP.body} gpo={draftP.gpo_no} placeholder="What went well yesterday…"
                onChange={setDraftP} onSubmit={() => draftP.body || draftP.gpo_no ? addPositive(draftP) : null} />
              <button style={{ ...btnP, marginTop: 6 }} disabled={busy || (!draftP.body && !draftP.gpo_no)}
                onClick={() => addPositive(draftP)}>+ Add</button>
            </div>
          )}
        </Row>

        <Row label={L.stuck}>
          {/* CLOSED ITEMS ARE NOT STUCK. blockersSql deliberately returns resolved rows so a
              person can see what they shut today — that is right — but rendering them inside
              STUCK made five "resolved · DONE" lines read as five live blockers on the Accounts
              sheet. The data was never wrong; the heading was. Split by resolved_at, order
              unchanged within each group, and nothing is hidden. */}
          {d.blockers.filter(b => !b.resolved_at).map(b => <ItemRow key={b.id} it={b} kind="blockers" locked={locked} onDone={load} setErr={setErr} retarget />)}
          {d.blockers.filter(b => !b.resolved_at).length === 0 && d.blockers.length > 0 && (
            <div style={{ fontSize: 12, color: C.muted, fontStyle: 'italic' }}>Nothing still stuck — closed items are below.</div>
          )}
          {d.blockers.some(b => b.resolved_at) && (
            <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px dashed ' + C.border }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.green, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4 }}>
                Closed today · {d.blockers.filter(b => b.resolved_at).length}
              </div>
              {d.blockers.filter(b => b.resolved_at).map(b => <ItemRow key={b.id} it={b} kind="blockers" locked={locked} onDone={load} setErr={setErr} retarget />)}
            </div>
          )}
          {!locked && (
            <div style={{ marginTop: 6 }}>
              <LineInput value={draft.body} gpo={draft.gpo_no} placeholder="What is stuck, pending or coming up…"
                onChange={p => setDraft(v => ({ ...v, ...p }))}
                onSubmit={() => draft.body || draft.gpo_no ? addBlocker(draft) : null}
                after={
                  /* WHO IT WAITS ON, WHILE YOU ARE WRITING IT. Optional — plenty of stuck
                     items are nobody else's to clear — so it opens on "waiting on nobody"
                     and never blocks + Add. The same picker stays on the saved row for
                     changing it later. */
                  <select value={draft.owed_by_user_id || ''}
                    onChange={ev => setDraft(v => ({ ...v, owed_by_user_id: ev.target.value || null }))}
                    title="Who are you waiting on? Optional."
                    style={{ ...input, flex: '0 1 190px', color: draft.owed_by_user_id ? C.navy : C.muted,
                      borderColor: draft.owed_by_user_id ? C.navy : undefined }}>
                    <option value="">{roster === null ? 'loading…' : '◎ waiting on nobody'}</option>
                    {(roster || []).map(u => (
                      <option key={u.id} value={u.id}>{u.name}{u.department ? ` — ${u.department}` : ''}</option>
                    ))}
                  </select>
                } />
              <button style={{ ...btnP, marginTop: 6 }} disabled={busy || (!draft.body && !draft.gpo_no)}
                onClick={() => addBlocker(draft)}>+ Add</button>
            </div>
          )}
          <Suggestions sug={sug} onAdd={(s) => addBlocker(s)} locked={locked} />
        </Row>

        <Row label={L.target} last>
          {d.targets.map(t => <ItemRow key={t.id} it={t} kind="targets" locked={locked} onDone={load} setErr={setErr} />)}
          {!locked && (
            <div style={{ marginTop: 6 }}>
              <LineInput value={draftT.body} gpo={draftT.gpo_no} placeholder="What you will finish today…"
                onChange={setDraftT} onSubmit={() => draftT.body || draftT.gpo_no ? addTarget(draftT) : null} />
              <button style={{ ...btnP, marginTop: 6 }} disabled={busy || (!draftT.body && !draftT.gpo_no)}
                onClick={() => addTarget(draftT)}>+ Add</button>
            </div>
          )}
        </Row>
      </div>

      {err && <div style={{ color: C.red, marginTop: 10, fontSize: 12 }}>{err}</div>}
      {!locked && (
        <div style={{ marginTop: 14, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <button style={{ ...btnP, padding: '10px 20px', fontSize: 13,
            background: e.submitted_at ? C.green : C.navy }} disabled={busy}
            onClick={submitSheet}>
            {e.submitted_at ? '✓ Submitted — save again' : 'Submit my sheet'}
          </button>
          {(!isEmptyDraft(draft) || !isEmptyDraft(draftT)) && (
            <span style={{ fontSize: 12, color: C.amber }}>
              ⚠ You have text not yet added — Submit will add it.
            </span>
          )}
        </div>
      )}
      {e.edited_late && <div style={{ marginTop: 8, fontSize: 11, color: C.amber }}>
        ⏱ This sheet was edited after {d.clock.soft_lock_at} and is stamped as a late edit.</div>}
    </div>
  );
}

const HeadCell = ({ label, children, border }) => (
  <div style={{ padding: '8px 12px', borderRight: border ? '1px solid #333' : 'none', borderLeft: border ? '1px solid #333' : 'none' }}>
    <div style={{ fontSize: 9, letterSpacing: 1, color: C.muted, fontWeight: 700, textTransform: 'uppercase' }}>{label}</div>
    {children}
  </div>
);

const Row = ({ label, children, last }) => (
  <div style={{ borderBottom: last ? 'none' : '1px solid #333' }}>
    <div style={{ background: C.bg, padding: '6px 12px', fontSize: 11, fontWeight: 700, letterSpacing: 0.3, borderBottom: '1px solid #eee' }}>{label}</div>
    <div style={{ padding: '10px 12px' }}>{children}</div>
  </div>
);

const Box = ({ title, children, tone }) => (
  <div className="hd-noprint" style={{ border: '1px solid ' + (tone === 'amber' ? '#F0D9A8' : C.border),
    background: tone === 'amber' ? '#FFFBF2' : '#fff', borderRadius: 8, padding: 14, marginBottom: 14 }}>
    <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>{title}</div>
    {children}
  </div>
);

// The reopen control (mig 209) lives HERE — on the bar that says the sheet is shut, which is
// the only moment anyone wants it. Admin only, and only when there is something to reopen.
function PhaseBar({ d, isAdmin, onChanged }) {
  const map = { open: ['Open for entry', C.green], grace: [`Grace period — edits until ${d.clock.grace_until} are stamped late`, C.amber],
    locked: ['Locked — the meeting has started', C.red], closed: ['Closed', C.muted],
    reopened: ['Reopened — open until someone closes it', C.amber] };
  const [txt, col] = map[d.phase] || ['—', C.muted];
  const [busy, setBusy] = useState(false);
  const shut = d.phase === 'locked' || d.phase === 'closed';
  const act = async (path, body) => {
    setBusy(true);
    try { await api.post(path, body || {}); onChanged?.(); }
    catch (e) { window.alert(e.response?.data?.error || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="hd-noprint" style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12, fontSize: 12 }}>
      <span style={{ color: col, fontWeight: 700 }}>● {txt}</span>
      <span style={{ color: C.muted }}>IST now {d.clock.ist_now}</span>
      <span style={{ color: C.muted }}>Soft lock {d.clock.soft_lock_at} · huddle {d.clock.meeting_starts_at}</span>
      {isAdmin && shut && (
        <button disabled={busy} onClick={() => {
          if (!window.confirm(`Reopen ${d.date} for edits?\n\nIt stays open until someone closes it — there is no auto re-lock. Anything written now is marked as written after the lock.`)) return;
          act(`/huddle/${d.date}/reopen`);
        }} style={{ ...btn, background: '#FEF3C7', color: '#8A5A00', border: '1px solid #fde68a' }}>🔓 Reopen this day</button>
      )}
      {isAdmin && d.phase === 'reopened' && (
        <button disabled={busy} onClick={() => act(`/huddle/${d.date}/agenda`, { status: 'closed' })}
          style={{ ...btn, background: '#F3F4F6', color: C.muted, border: '1px solid #ddd' }}>🔒 Close it again</button>
      )}
      <span style={{ marginLeft: 'auto', color: C.muted }}>{d.counts.submitted} of {d.counts.roster} submitted</span>
    </div>
  );
}

// Yesterday's target → done / not done (+ required reason).
function CarryRow({ t, locked, onDone, setErr }) {
  const [reason, setReason] = useState('');
  const [asking, setAsking] = useState(false);
  const mark = async (outcome) => {
    if (outcome === 'not_done' && !reason.trim()) { setAsking(true); return; }
    try { await api.post(`/huddle/targets/${t.id}/outcome`, { outcome, reason: reason.trim() }); onDone(); }
    catch (e) { setErr(e.response?.data?.error || 'Failed'); }
  };
  const done = t.outcome !== 'pending';
  return (
    <div style={{ padding: '7px 0', borderTop: '1px solid #F0E6D2' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {t.gpo_no && <strong style={{ fontFamily: 'monospace', color: C.red, fontSize: 12 }}>{t.gpo_no}</strong>}
        <span style={{ flex: 1, fontSize: 13 }}>{t.body || <em style={{ color: C.muted }}>(GPO only)</em>}
          {(t.style_no || t.colour) && <span style={{ color: C.muted, fontSize: 11 }}> · {[t.style_no, t.colour].filter(Boolean).join(' · ')}</span>}
          <CarryBadge n={t.carry_count} />
        </span>
        {done ? (
          <span style={{ fontSize: 12, fontWeight: 700, color: t.outcome === 'done' ? C.green : C.amber }}>
            {t.outcome === 'done' ? '✓ done' : '↻ not done — carried into stuck'}</span>
        ) : !locked && (
          <span style={{ display: 'flex', gap: 6 }}>
            <button style={{ ...btn, background: C.green, color: '#fff' }} onClick={() => mark('done')}>✓ Done</button>
            <button style={{ ...btn, background: C.amber, color: '#fff' }} onClick={() => mark('not_done')}>Not done</button>
          </span>
        )}
      </div>
      {asking && !done && (
        <div style={{ marginTop: 6, display: 'flex', gap: 6 }}>
          <input style={{ ...input, flex: 1 }} autoFocus placeholder="Why not? (required)" value={reason}
            onChange={e => setReason(e.target.value)} onKeyDown={e => e.key === 'Enter' && mark('not_done')} />
          <button style={{ ...btnP, background: C.amber }} onClick={() => mark('not_done')}>Carry it</button>
        </div>
      )}
    </div>
  );
}


// ── MY DAY ──────────────────────────────────────────────────────────────────
// Shares the /huddle/today payload with the sheet: targets, blockers and owed items are all
// already in one call, so the grid adds a read of its own slots and nothing else.
function MyDay() {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get('/huddle/today').then(r => setD(r.data.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <div style={{ color: C.muted, fontSize: 13 }}>Loading…</div>;
  if (!d.entry) return <div style={{ color: C.muted, fontSize: 13 }}>You have no sheet today.</div>;
  return (
    <div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>
        {fmtD(d.date)} · slotting stays open all day — it is not a revision of the commitment.
      </div>
      <HuddleGrid date={d.date} entryId={d.entry.id} targets={d.targets} blockers={d.blockers}
        owed={d.owed_to_me || []} onChanged={load} />
    </div>
  );
}

// ── OWED BY YOU (mig 208) ───────────────────────────────────────────────────
// A FOURTH block, above the three own-sections, where carry_in already sits. Not merged into
// the reader's own Stuck list: ruling 1 says the sheet is the person's own words, and this is
// someone else's claim about them. Stuck answers "what is holding me up"; this answers "what
// I am holding up for someone else". Opposite directions.
function OwedRow({ it, onDone, setErr }) {
  const clear = async () => {
    const note = window.prompt('What did you do? (required)');
    if (note === null) return;
    if (!note.trim()) { setErr('Say what you did — a note is required.'); return; }
    try { await api.post(`/huddle/blockers/${it.id}/clear`, { note: note.trim() }); onDone(); }
    catch (e) { setErr(e.response?.data?.error || 'Failed'); }
  };
  const rebut = async () => {
    const body = window.prompt('What are YOU waiting on? This raises your own item pointing back — it does not delete theirs.');
    if (body === null) return;
    if (!body.trim()) { setErr('Say what you are waiting on.'); return; }
    try { await api.post(`/huddle/blockers/${it.id}/rebut`, { body: body.trim() }); onDone(); }
    catch (e) { setErr(e.response?.data?.error || 'Failed'); }
  };
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '6px 0', borderBottom: '1px dotted #eee', flexWrap: 'wrap' }}>
      {it.gpo_no && <strong style={{ fontFamily: 'monospace', color: C.red, fontSize: 12 }}>{it.gpo_no}</strong>}
      <span style={{ flex: 1, fontSize: 13 }}>
        {it.body || <em style={{ color: C.muted }}>(GPO only)</em>}
        <span style={{ color: C.muted, fontSize: 11 }}> · raised by {it.raised_by_name}
          {it.raised_by_dept ? ` (${it.raised_by_dept})` : ''} on {fmtD(it.raised_on)}</span>
      </span>
      {/* THE WAIT LINE. Contested items carry NO wait count — who is waiting is the thing under
          dispute, so claiming a number would assert what the data no longer supports. Otherwise
          the owed clock is shown, with the item's TOTAL age beside it when they differ (a
          re-route restarts the first but never the second). */}
      {it.contested
        ? <span style={{ fontSize: 11, fontWeight: 700, color: C.amber }}>disputed</span>
        : <span style={{ fontSize: 11, fontWeight: 700, color: it.owed_days >= 3 ? C.red : C.muted }}>
            waiting {it.owed_days}d{it.total_days > it.owed_days ? ` · raised ${it.total_days}d ago` : ''}
          </span>}
      {it.cleared_at
        ? <span style={{ fontSize: 10, color: C.green, background: '#EAF7EE', borderRadius: 10, padding: '1px 7px' }}>
            you marked it cleared — with {it.raised_by_name} to confirm</span>
        : <>
            <button className="hd-noprint" style={{ ...btn, background: '#EAF7EE', color: C.green }} onClick={clear}>✓ Cleared</button>
            <button className="hd-noprint" style={{ ...btn, background: '#FEF3C7', color: '#8A5A00' }} onClick={rebut}>⇄ Not mine</button>
          </>}
    </div>
  );
}

function ItemRow({ it, kind, locked, onDone, setErr, retarget }) {
  // WHO IT WAITS ON — an in-page select, not window.prompt (2026-08-17).
  //
  // It was a prompt listing 19 names to be chosen by typing a number. Two things were wrong
  // with that. Nineteen numbered options in a modal dialog is unusable even when it works;
  // and prompt() returns null both when the user cancels AND when the browser has suppressed
  // dialogs — the "prevent this page from creating additional dialogs" checkbox, which any
  // user hits after a few prompts. The handler treated null as "cancelled" and returned
  // silently, so a suppressed dialog looked exactly like a picker that refuses to accept a
  // selection. That is what Merchant 1 reported, and there was no error to see.
  const [picking, setPicking] = useState(false);
  const [roster, setRoster] = useState(null);   // null = not loaded yet
  const openPicker = async () => {
    setPicking(true);
    if (roster) return;
    try {
      const r = await api.get('/huddle/roster');
      setRoster(r.data.data || []);
    } catch (e) { setErr(e.response?.data?.error || 'Could not load the roster'); setPicking(false); }
  };
  const setOwed = async (v) => {
    setPicking(false);
    try { await api.post(`/huddle/blockers/${it.id}/owe`, { owed_by_user_id: v || null }); onDone(); }
    catch (e) { setErr(e.response?.data?.error || 'Failed'); }
  };
  const del = async () => {
    try { await api.delete(`/huddle/${kind}/${it.id}`); onDone(); }
    catch (e) { setErr(e.response?.data?.error || 'Failed'); }
  };
  const again = async () => {
    try { await api.post(`/huddle/blockers/${it.id}/retarget`); onDone(); }
    catch (e) { setErr(e.response?.data?.error || 'Failed'); }
  };
  // Resolve (mig 207). A note is REQUIRED — the same rule as the reason on a not_done
  // target, and the DB constraint refuses a resolution without one. Cancelling the prompt
  // does nothing; an empty note is not sent.
  // THE RAISER'S SIDE of the two-step. A pending clear must not sit silently: it ages here
  // as "X says this is cleared — confirm or say why not", because with no auto-confirm the
  // obligation has moved to the raiser and the sheet has to say so.
  const rejectClear = async () => {
    const note = window.prompt('Why is it not cleared? (required)');
    if (note === null) return;
    if (!note.trim()) { setErr('Say why it is not cleared — a note is required.'); return; }
    try { await api.post(`/huddle/blockers/${it.id}/reject-clear`, { note: note.trim() }); onDone(); }
    catch (e) { setErr(e.response?.data?.error || 'Failed'); }
  };
  const resolve = async () => {
    const note = window.prompt('How was it resolved? (required)');
    if (note === null) return;
    if (!note.trim()) { setErr('Say how it was resolved — a note is required.'); return; }
    try { await api.post(`/huddle/blockers/${it.id}/resolve`, { note: note.trim() }); onDone(); }
    catch (e) { setErr(e.response?.data?.error || 'Failed'); }
  };
  // THE ONE WAY PAST A STILL-OPEN TASK, and the refusal names it out loud: say what you
  // resolved that ISN'T the task. The task is not touched — closing it belongs in /tasks,
  // where a completion carries a note, proof and an audit. The row is stamped
  // resolved_off_task so it never reads as though the task got done.
  const resolveOffTask = async () => {
    const note = window.prompt(
      `${it.task_no} stays open. What did you resolve, if not the task? (required)`);
    if (note === null) return;
    if (!note.trim()) { setErr('Say what you resolved — a note is required.'); return; }
    try { await api.post(`/huddle/blockers/${it.id}/resolve`, { note: note.trim(), not_the_task: true }); onDone(); }
    catch (e) { setErr(e.response?.data?.error || 'Failed'); }
  };
  // TWO DIFFERENT FACTS, one column (mig 249). promoted_at = this item BECAME a task and the
  // huddle no longer owns it, so its controls go away — the pre-existing rule, unchanged.
  // taskOpen = it came FROM a task that is still open, so only RESOLVE is withheld; routing
  // it, carrying it and putting it back on today's target row all stay available.
  const promoted = !!it.promoted_at;
  const taskOpen = !!it.task_no && !!it.task_open;
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '5px 0', borderBottom: '1px dotted #eee', flexWrap: 'wrap' }}>
      {it.gpo_no && <strong style={{ fontFamily: 'monospace', color: C.red, fontSize: 12 }}>{it.gpo_no}</strong>}
      <span style={{ flex: 1, fontSize: 13 }}>{it.body || <em style={{ color: C.muted }}>(GPO only)</em>}
        {(it.style_no || it.colour) && <span style={{ color: C.muted, fontSize: 11 }}> · {[it.style_no, it.colour].filter(Boolean).join(' · ')}</span>}
        <CarryBadge n={it.carry_count} />
        <TaskChip it={it} />
      </span>
      {/* A resolved item stays on its own day, struck through and named — the register keeps
          the outcome rather than the row disappearing. It is excluded from tomorrow's carry. */}
      {it.resolved_at && (
        <span style={{ fontSize: 10, borderRadius: 10, padding: '1px 7px',
          color: it.resolved_off_task ? '#8A5A00' : C.green,
          background: it.resolved_off_task ? '#FEF3C7' : '#EAF7EE' }}>
          resolved{it.resolved_off_task ? ' · not the task' : ''}{it.resolved_by_name ? ` · ${it.resolved_by_name}` : ''}{it.resolved_note ? ` — ${it.resolved_note}` : ''}
        </span>
      )}
      {/* Who it waits on, and the two-clock line. A contested item shows no wait count. */}
      {retarget && it.owed_by_name && (
        it.orphaned
          /* The owed party has left the standup. The clock STOPPED at removal — ageing a wait
             against someone who cannot act is a false accusation — and the row says plainly
             that it needs a new owner rather than quietly clearing itself. */
          ? <span style={{ fontSize: 10, color: '#8A5A00', background: '#FEF3C7', borderRadius: 10, padding: '1px 7px' }}>
              was waiting on {it.owed_by_name} — no longer on the huddle · re-route it
            </span>
          : <span style={{ fontSize: 10, color: C.navy, background: '#EEF0FA', borderRadius: 10, padding: '1px 7px' }}>
              waiting on {it.owed_by_name}
              {it.contested ? ` · disputed since ${fmtD(it.disputed_since)}`
                : ` · ${it.owed_days}d${it.total_days > it.owed_days ? ` (raised ${it.total_days}d ago)` : ''}`}
            </span>
      )}
      {/* A PENDING CLEAR SURVIVES REMOVAL. They did the work before they left; the raiser can
          still confirm it. Wiping it would destroy the last thing that person recorded. */}
      {retarget && it.orphaned && it.cleared_at && (
        <span style={{ fontSize: 10, color: C.green, background: '#EAF7EE', borderRadius: 10, padding: '1px 7px' }}>
          they marked it cleared before leaving — you can still confirm
        </span>
      )}
      {retarget && it.cleared_at && !it.resolved_at && (
        <span style={{ fontSize: 10, color: '#8A5A00', background: '#FEF3C7', borderRadius: 10, padding: '1px 7px' }}>
          {it.cleared_by_name} says this is cleared — confirm or say why not
        </span>
      )}
      {!locked && retarget && !it.resolved_at && !promoted && (
        picking ? (
          <select className="hd-noprint" autoFocus defaultValue={it.owed_by_user_id || ''}
            onChange={e => setOwed(e.target.value)} onBlur={() => setPicking(false)}
            style={{ ...btn, background: '#fff', color: C.navy, border: `1px solid ${C.navy}`, maxWidth: 240 }}>
            <option value="">{roster === null ? 'loading…' : '— nobody / clear —'}</option>
            {(roster || []).map(u => (
              <option key={u.id} value={u.id}>{u.name}{u.department ? ` — ${u.department}` : ''}</option>
            ))}
          </select>
        ) : (
          <button className="hd-noprint" style={{ ...btn, background: '#EEF0FA', color: C.navy }} onClick={openPicker}>◎ Waiting on…</button>
        )
      )}
      {!locked && retarget && it.cleared_at && !it.resolved_at && <button className="hd-noprint" style={{ ...btn, background: '#FEF3C7', color: '#8A5A00' }} onClick={rejectClear}>✕ Not cleared</button>}
      {!locked && retarget && !it.resolved_at && !promoted && !taskOpen && <button className="hd-noprint" style={{ ...btn, background: '#EAF7EE', color: C.green }} onClick={resolve}>{it.cleared_at ? '✓ Confirm resolved' : '✓ Resolved'}</button>}
      {/* The refusal, in place of the button it replaces — the chip above it is already
          showing the task pending, its progress and how late it is. */}
      {!locked && retarget && !it.resolved_at && !promoted && taskOpen && (
        <button className="hd-noprint" title={`${it.task_no} is still open. Close it in Tasks, or record here what you resolved that is not the task.`}
          style={{ ...btn, background: '#FEF3C7', color: '#8A5A00' }} onClick={resolveOffTask}>
          ✓ Resolved — not the task
        </button>
      )}
      {!locked && retarget && !promoted && !it.resolved_at && <button className="hd-noprint" style={{ ...btn, background: '#EEF0FA', color: C.navy }} onClick={again}>↑ Make it today's target</button>}
      {!locked && !it.resolved_at && <button className="hd-noprint" style={{ ...btn, background: 'transparent', color: C.muted }} onClick={del}>✕</button>}
    </div>
  );
}

// Suggestions — never auto-added. Personal items are named; role queues are a SEPARATE,
// LABELLED block, because a role queue presented as yours is a queue nobody owns.
function Suggestions({ sug, onAdd, locked }) {
  if (locked) return null;
  const has = sug.personal.length || sug.role_queues.length;
  if (!has) return null;
  return (
    <div className="hd-noprint" style={{ marginTop: 12, borderTop: '1px dashed #ddd', paddingTop: 10 }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, color: C.muted, textTransform: 'uppercase' }}>
        Suggestions — yours to confirm or ignore
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
        {sug.personal.map(s => (
          <button key={s.kind + s.ref} style={{ ...btn, background: '#F3F4F6', color: C.text, fontWeight: 500 }}
            onClick={() => onAdd({ body: s.text, source: s.source, source_ref: String(s.ref) })}>+ {s.text}</button>
        ))}
      </div>
      {sug.role_queues.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, color: C.amber, textTransform: 'uppercase' }}>
            Your role's queues — not assigned to you personally
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
            {sug.role_queues.map(s => (
              <button key={s.label} style={{ ...btn, background: '#FEF3E2', color: C.amber, fontWeight: 500 }}
                onClick={() => onAdd({ body: s.label, source: s.source })}>+ {s.label}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── FACILITATOR — the projected screen, built for 55 seconds a person ───────
function Facilitator({ canSeeAll }) {
  const [d, setD] = useState(null);
  const [prod, setProd] = useState(null);
  const [i, setI] = useState(0);
  const [err, setErr] = useState('');
  const [gridFor, setGridFor] = useState(null);   // whose day is expanded, read-only
  const load = useCallback(async () => {
    const r = await api.get('/huddle/facilitator').catch(e => { setErr(e.response?.data?.error || 'Not available'); return null; });
    if (r) setD(r.data.data);
  }, []);
  useEffect(() => { load(); api.get('/huddle/production-panel').then(r => setProd(r.data.data)).catch(() => {}); }, [load]);

  const list = useMemo(() => d?.entries || [], [d]);
  const mark = useCallback(async (uid, attendance) => {
    try {
      await api.post(`/huddle/${d.date}/attendance`, { user_id: uid, attendance });
      setD(p => ({ ...p, entries: p.entries.map(x => x.user_id === uid ? { ...x, attendance } : x) }));
    } catch (e) { setErr(e.response?.data?.error || 'Failed'); }
  }, [d]);

  // Keyboard-first: space advances, P/A/L/E mark. Nothing needs the mouse — that is what
  // makes 55 seconds a person survivable.
  useEffect(() => {
    const onKey = (ev) => {
      if (!list.length || ['INPUT', 'TEXTAREA'].includes(ev.target.tagName)) return;
      const cur = list[i];
      if (ev.code === 'Space') { ev.preventDefault(); setI(x => Math.min(x + 1, list.length - 1)); }
      else if (ev.key === 'ArrowUp') { ev.preventDefault(); setI(x => Math.max(x - 1, 0)); }
      else if (ev.key === 'ArrowDown') { ev.preventDefault(); setI(x => Math.min(x + 1, list.length - 1)); }
      else if (cur && 'pale'.includes(ev.key.toLowerCase()) && ev.key.length === 1) {
        const m = { p: 'present', a: 'absent', l: 'late', e: 'excused' }[ev.key.toLowerCase()];
        if (m) { mark(cur.user_id, m); setI(x => Math.min(x + 1, list.length - 1)); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [list, i, mark]);

  if (err) return <div style={{ color: C.red }}>{err}</div>;
  if (!d) return <div style={{ color: C.muted }}>Loading…</div>;
  const agenda = async (patch) => { await api.post(`/huddle/${d.date}/agenda`, patch).catch(reportError('update the agenda')); load(); };

  return (
    <div>
      <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12, fontSize: 12 }}>
        <strong style={{ fontSize: 15 }}>HUDDLE — {fmtD(d.date)}</strong>
        <span style={{ color: C.muted }}>IST {d.clock.ist_now}</span>
        <span style={{ color: C.green, fontWeight: 700 }}>● {d.counts.submitted} of {d.counts.roster} submitted</span>
        <span style={{ color: C.navy }}>{d.counts.marked} marked · {d.counts.present} present</span>
        <span style={{ marginLeft: 'auto', color: C.muted }}>
          Facilitator: <strong>{d.facilitator_chain.find(u => u.id === d.day.facilitator_id)?.name
            || d.facilitator_chain[0]?.name || '—'}</strong>
          {d.can_claim && <button style={{ ...btn, marginLeft: 8, background: '#EEF0FA', color: C.navy }}
            onClick={() => api.post(`/huddle/${d.date}/claim`).then(load).catch(reportError('take the facilitator role', load))}>Take it</button>}
        </span>
      </div>
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 10 }}>
        Chain: {d.facilitator_chain.map(u => u.name).join(' → ')} · whoever is present takes it.
        &nbsp;·&nbsp; <strong>Space</strong> next · <strong>P/A/L/E</strong> present, absent, late, excused
      </div>

      {/* Agenda 1 + 2 */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <AgendaChip n="1" label="Affirmations" done={d.day.affirmations_done} onClick={() => agenda({ affirmations_done: !d.day.affirmations_done })} />
        <AgendaChip n="2" label="Production bulletin — read by PPC" done={d.day.bulletin_done} onClick={() => agenda({ bulletin_done: !d.day.bulletin_done })} />
      </div>
      <ProductionPanel prod={prod} />

      {/* 3. THE ROUND */}
      <div style={{ border: '1px solid ' + C.border, borderRadius: 10, overflow: 'hidden' }}>
        {list.map((p, idx) => {
          const on = idx === i;
          return (
            <div key={p.id} onClick={() => setI(idx)} style={{ padding: on ? '14px 16px' : '8px 16px', cursor: 'pointer',
              background: on ? '#FBFBFE' : '#fff', borderBottom: '1px solid #f2f2f2',
              borderLeft: '4px solid ' + (on ? C.navy : 'transparent') }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <strong style={{ fontSize: on ? 15 : 13 }}>{on ? '▸ ' : ''}{p.name}</strong>
                <span style={{ fontSize: 11, color: C.muted }}>{p.department || p.role}</span>
                {!p.submitted_at && <span style={{ fontSize: 10, color: C.amber, background: '#FEF3E2', borderRadius: 10, padding: '1px 8px' }}>not submitted</span>}
                {p.edited_late && <span style={{ fontSize: 10, color: C.muted }}>⏱ late edit</span>}
                <span style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
                  {[['present', 'P', C.green], ['absent', 'A', C.red], ['late', 'L', C.amber], ['excused', 'E', C.muted]].map(([k, ch, col]) => (
                    <button key={k} onClick={ev => { ev.stopPropagation(); mark(p.user_id, k); }}
                      title={k} style={{ ...btn, padding: '3px 9px', background: p.attendance === k ? col : '#F3F4F6',
                        color: p.attendance === k ? '#fff' : '#888' }}>{ch}</button>
                  ))}
                </span>
              </div>
              {on && (
                <div style={{ marginTop: 8, fontSize: 13, display: 'grid', gap: 5 }}>
                  {/* A NEUTRAL BULLET, not a tick. The green ✔ that used to sit here was
                      decoration — printed even when the field was empty — while a real
                      "✔ done" reading huddle_targets.outcome sits a few lines below. Once
                      positives are line items the two would have been indistinguishable. */}
                  {(p.positives || []).map(pz => (
                    <div key={pz.id} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span style={{ color: C.green, fontWeight: 700 }}>+</span>
                      {pz.gpo_no && <strong style={{ fontFamily: 'monospace', color: C.red, fontSize: 12 }}>{pz.gpo_no}</strong>}
                      <span>{pz.body}</span><CarryBadge n={pz.carry_count} />
                    </div>
                  ))}
                  {!(p.positives || []).length && <div style={{ color: C.muted }}><span style={{ fontWeight: 700 }}>+</span> <em>—</em></div>}
                  {p.blockers.map(b => (
                    <div key={b.id} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ color: C.amber, fontWeight: 700 }}>⚠</span>
                      {b.gpo_no && <strong style={{ fontFamily: 'monospace', color: C.red, fontSize: 12 }}>{b.gpo_no}</strong>}
                      <span>{b.body}</span><CarryBadge n={b.carry_count} />
                      {b.task_no
                        ? <TaskChip it={b} />
                        : canSeeAll && <PromoteButton blocker={b} onDone={load} />}
                    </div>
                  ))}
                  {p.targets.map(t => (
                    <div key={t.id} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span style={{ color: C.navy, fontWeight: 700 }}>→</span>
                      {t.gpo_no && <strong style={{ fontFamily: 'monospace', color: C.red, fontSize: 12 }}>{t.gpo_no}</strong>}
                      <span>{t.body}</span><CarryBadge n={t.carry_count} />
                    </div>
                  ))}
                  {/* THE FACILITATOR'S READ-ONLY GRID (mig 210). The route enforced canSeeAll
                      and returned read_only, and HuddleGrid disabled every control — but there
                      was no way to reach it. Flagged twice as plumbed-but-unsurfaced; this is
                      the button. Behind a toggle so the projected screen does not double in
                      height for every person on the roster. */}
                  <div>
                    <button onClick={() => setGridFor(gridFor === p.user_id ? null : p.user_id)}
                      style={{ ...btn, background: '#EEF0FA', color: C.navy, fontSize: 11 }}>
                      {gridFor === p.user_id ? '▾ Hide their day' : '🗓 Their day'}
                    </button>
                  </div>
                  {gridFor === p.user_id && (
                    <div style={{ marginTop: 6, borderTop: '1px dashed #ddd', paddingTop: 8 }}>
                      <HuddleGrid date={d.date} userId={p.user_id} readOnly
                        targets={p.targets || []} blockers={p.blockers || []} owed={[]} />
                    </div>
                  )}
                  {p.yesterday.length > 0 && (
                    <div style={{ fontSize: 11, color: C.muted }}>
                      Yesterday: {p.yesterday.map((y, k) => (
                        <span key={k} style={{ marginRight: 8 }}>
                          {y.outcome === 'done' ? '✔ done' : y.outcome === 'not_done' ? `↻ carried ${y.carry_count}×` : '· not reported'}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
        <button style={{ ...btn, background: '#F3F4F6', color: C.text }} onClick={() => agenda({ status: 'locked' })}>Lock entry</button>
        <button style={{ ...btnP, background: C.green }} onClick={() => agenda({ status: 'closed' })}>Close the huddle</button>
      </div>
    </div>
  );
}

const AgendaChip = ({ n, label, done, onClick }) => (
  <button onClick={onClick} style={{ ...btn, padding: '9px 14px', border: '1px solid ' + (done ? C.green : '#ddd'),
    background: done ? '#EAF7EE' : '#fff', color: done ? C.green : C.text }}>
    {done ? '✓' : n + '.'} {label}
  </button>
);

function PromoteButton({ blocker, onDone }) {
  const go = async () => {
    const deadline = window.prompt(`Promote to a task — this creates a real, owned, audited task.\n\n"${blocker.body || blocker.gpo_no}"\n\nDue date (YYYY-MM-DD):`);
    if (!deadline) return;
    try { const r = await api.post(`/huddle/blockers/${blocker.id}/promote`, { deadline });
      window.alert(`Created ${r.data.data.task.task_no}.`); onDone(); }
    catch (e) { window.alert(e.response?.data?.error || 'Promote failed'); }
  };
  return <button style={{ ...btn, background: '#EEF0FA', color: C.navy, padding: '2px 9px' }} onClick={go}>promote to task</button>;
}

// Agenda item 2. Reads the REAL plan tables and says so when they are empty — it never
// shows a zero it did not measure, and never claims to be "the bulletin".
function ProductionPanel({ prod }) {
  if (!prod) return null;
  const secs = [['Cutting', prod.cutting], ['Stitching', prod.stitching], ['Finishing', prod.finishing]];
  return (
    <div style={{ border: '1px solid ' + C.border, borderRadius: 10, padding: 14, marginBottom: 14, background: '#fff' }}>
      <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Production — what the ERP knows for {fmtD(prod.date)}</div>
      {!prod.has_data ? (
        <div style={{ fontSize: 12, color: C.muted }}>
          {prod.note}
          {prod.last_plan_date && <> The most recent plan on record is <strong>{fmtD(prod.last_plan_date)}</strong>.</>}
        </div>
      ) : secs.filter(([, r]) => r.length).map(([name, rows]) => (
        <div key={name} style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.navy }}>{name}</div>
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <thead><tr style={{ color: C.muted }}>{['Line', 'GPO', 'Style', 'Target', 'Actual', 'Rejects', 'Rework'].map(h =>
              <th key={h} style={{ textAlign: 'left', padding: '3px 6px', fontWeight: 600 }}>{h}</th>)}</tr></thead>
            <tbody>{rows.map((r, k) => (
              <tr key={k}>
                <td style={{ padding: '3px 6px' }}>{r.line_no}</td>
                <td style={{ padding: '3px 6px', fontFamily: 'monospace', color: C.red }}>{r.gpo_no || '—'}</td>
                <td style={{ padding: '3px 6px' }}>{r.style || '—'}</td>
                <td style={{ padding: '3px 6px' }}>{r.target_qty}</td>
                <td style={{ padding: '3px 6px', fontWeight: 700 }}>{r.actual_qty}</td>
                <td style={{ padding: '3px 6px', color: r.rejected_qty ? C.red : C.muted }}>{r.rejected_qty}</td>
                <td style={{ padding: '3px 6px', color: r.rework_qty ? C.amber : C.muted }}>{r.rework_qty}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

// ─── PRINT — page 1 the form, page 2 (the reverse) the affirmations ──────────
function PrintView() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get('/huddle/print').then(r => setD(r.data.data)).catch(() => {}); }, []);
  if (!d) return <div style={{ color: C.muted }}>Loading…</div>;
  const e = d.entry;
  const cell = { border: '1.5px solid #000', padding: '8px 10px' };
  // A sheet with nothing on it is not a form to take into the meeting, it is a blank page.
  // Production printed one three times before anyone noticed.
  // "Empty sheet" now means no ITEMS in any of the three sections. Reading entry.positive
  // here would call a sheet empty the moment positives became line items.
  const isEmpty = !(d.positives || []).length && !d.targets.length && !d.blockers.length;
  if (isEmpty) {
    return (
      <div style={{ border: '1px solid ' + C.border, borderRadius: 10, padding: 28, textAlign: 'center', background: '#fff' }}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>There is nothing on this sheet yet.</div>
        <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6 }}>
          Fill in <strong>My Sheet</strong> first — your positive from yesterday, what is stuck, and today's target.<br />
          Printing a blank form does not put it on the record.
        </div>
      </div>
    );
  }
  return (
    <div>
      <button className="hd-noprint" style={{ ...btnP, marginBottom: 12 }} onClick={() => window.print()}>🖨 Print this sheet</button>
      <div className="hd-print">
        {/* PAGE 1 — the form */}
        <div className="hd-page-1" style={{ border: '1.5px solid #000' }}>
          <div className="hd-head" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr' }}>
            <div style={{ ...cell, borderTop: 'none', borderLeft: 'none', borderBottom: 'none' }}>
              <Lbl>{L.header[0]}</Lbl><div style={{ fontSize: 15, fontWeight: 700 }}>{e.department || ''}</div></div>
            <div style={{ ...cell, borderTop: 'none', borderBottom: 'none' }}>
              <Lbl>{L.header[1]}</Lbl><div style={{ fontSize: 15, fontWeight: 700 }}>{fmtD(d.date)}</div></div>
            <div style={{ ...cell, borderTop: 'none', borderRight: 'none', borderBottom: 'none' }}>
              <Lbl>{L.header[2]}</Lbl><div style={{ fontSize: 15, fontWeight: 700 }}>{e.name}</div></div>
          </div>
          <PrintRow label={L.positive}>
            {/* Reads huddle_positives, not the superseded entry.positive blob — a printed
                sheet that omitted today's items would be worse than no printed sheet. */}
            {(d.positives || []).length ? d.positives.map((pz, k) => (
              <div key={pz.id} style={{ marginBottom: 3 }}>{k + 1}. {[pz.gpo_no, pz.body].filter(Boolean).join(' — ')}
                {pz.carry_count > 0 && <strong> (carried {pz.carry_count}×)</strong>}</div>
            )) : <span>&nbsp;</span>}
          </PrintRow>
          <PrintRow label={L.stuck}>
            {d.blockers.length ? d.blockers.map((b, k) => (
              <div key={b.id} style={{ marginBottom: 3 }}>{k + 1}. {[b.gpo_no, b.body].filter(Boolean).join(' — ')}
                {b.carry_count > 0 && <strong> (carried {b.carry_count}×)</strong>}</div>
            )) : <span>&nbsp;</span>}
          </PrintRow>
          <PrintRow label={L.target} last>
            {d.targets.length ? d.targets.map((t, k) => (
              <div key={t.id} style={{ marginBottom: 3 }}>{k + 1}. {[t.gpo_no, t.body].filter(Boolean).join(' — ')}
                {t.carry_count > 0 && <strong> (carried {t.carry_count}×)</strong>}</div>
            )) : <span>&nbsp;</span>}
          </PrintRow>
        </div>

        {/* PAGE 2 — the reverse of the sheet */}
        <div className="hd-page-2 hd-affirm-box" style={{ border: '1.5px solid #000', marginTop: 24, padding: 22, minHeight: 260 }}>
          <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: 2, textAlign: 'center', marginBottom: 14 }}>AFFIRMATIONS</div>
          {d.affirmations_set ? (
            <div className="hd-affirm" style={{ fontSize: 15, lineHeight: 1.9, whiteSpace: 'pre-wrap', textAlign: 'center' }}>{d.affirmations}</div>
          ) : (
            <div style={{ fontSize: 13, lineHeight: 1.9, textAlign: 'center', color: '#999' }}>
              <em>The affirmations have not been set yet.</em><br />
              An admin can paste them in under Huddle → ⚙️ Affirmations,<br />
              and they will appear here on every sheet from then on.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
const Lbl = ({ children }) => <div style={{ fontSize: 8.5, letterSpacing: 1, fontWeight: 700, textTransform: 'uppercase', color: '#555' }}>{children}</div>;
const PrintRow = ({ label, children, last }) => (
  <div style={{ borderTop: '1.5px solid #000' }}>
    <div style={{ padding: '5px 10px', fontSize: 10.5, fontWeight: 700, borderBottom: '1px solid #999' }}>{label}</div>
    <div style={{ padding: '10px', fontSize: 13, minHeight: last ? 78 : 62 }}>{children}</div>
  </div>
);

// ─── Affirmations config (ruling 11) — pasted in, never hardcoded ────────────

// ── ROSTER ADMIN (mig 209) ──────────────────────────────────────────────────
// Reaches GET/POST /huddle/admin/roster, which were built with no way in. Admin only —
// the same gate the routes enforce, so the tab never offers a screen that would 403.
//
// THE WARNING IS THE POINT. Removing someone with open owed items does not clear those
// items, it strands them, so the count is shown BEFORE the decision rather than discovered
// after — and the confirm names the number.
function RosterAdmin() {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const load = useCallback(() => api.get('/huddle/admin/roster')
    .then(r => setRows(r.data.data || [])).catch(e => setMsg(e.response?.data?.error || 'Not available')), []);
  useEffect(() => { load(); }, [load]);
  if (msg && !rows) return <div style={{ color: C.red, fontSize: 13 }}>{msg}</div>;
  if (!rows) return <div style={{ color: C.muted, fontSize: 13 }}>Loading…</div>;

  const toggle = async (r) => {
    const on = !r.on_roster;
    if (!on) {
      const strand = r.owed_open + r.cleared_unconfirmed;
      const warn = strand
        ? `\n\n⚠ ${r.owed_open} open item(s) are waiting on ${r.name}`
          + (r.cleared_unconfirmed ? `, ${r.cleared_unconfirmed} already marked cleared and awaiting confirmation` : '')
          + `.\nThose do NOT disappear — they stay on the raiser's sheet marked for re-routing, and appear on the pending-work tile.`
        : '\n\nNothing is currently waiting on them.';
      if (!window.confirm(`Remove ${r.name} from the huddle roster?${warn}\n\nPast sheets are kept. They simply stop getting new ones.`)) return;
    }
    setBusy(r.id);
    try { const x = await api.post('/huddle/admin/roster', { user_id: r.id, on_roster: on }); setMsg(x.data.data.note); load(); }
    catch (e) { setMsg(e.response?.data?.error || 'Failed'); }
    finally { setBusy(''); }
  };

  const th = { textAlign: 'left', padding: '7px 10px', fontSize: 11, color: C.muted, fontWeight: 700, textTransform: 'uppercase' };
  const td = { padding: '7px 10px', fontSize: 13, borderTop: '1px solid #F2F2F2' };
  return (
    <div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>
        The roster is an EXCLUSION list: everyone active is in unless named out, so a new joiner
        appears by default rather than being invisible until somebody remembers to add them.
      </div>
      {msg && <div style={{ fontSize: 12, color: C.navy, marginBottom: 8 }}>{msg}</div>}
      <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ background: '#F7F6F3' }}>
            {['Name', 'Role', 'Sheets', 'Submitted', 'Owed / awaiting confirm', 'On roster'].map(h => <th key={h} style={th}>{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} style={{ opacity: r.on_roster ? 1 : 0.55 }}>
                <td style={{ ...td, fontWeight: 600 }}>{r.name}</td>
                <td style={td}>{r.role}</td>
                <td style={td}>{r.sheets}</td>
                <td style={{ ...td, color: r.submitted === 0 ? C.amber : '#333', fontWeight: r.submitted === 0 ? 700 : 400 }}>
                  {r.submitted}{r.submitted === 0 && r.sheets > 0 ? ' — never' : ''}
                </td>
                <td style={td}>
                  {r.owed_open ? <span style={{ color: C.amber, fontWeight: 700 }}>{r.owed_open}</span> : <span style={{ color: '#bbb' }}>—</span>}
                  {r.cleared_unconfirmed ? <span style={{ color: C.green }}> · {r.cleared_unconfirmed} cleared</span> : null}
                </td>
                <td style={td}>
                  {r.excluded_by_role
                    /* A role exclusion is config, not a per-person decision — the button would
                       imply this screen can undo it, and it cannot. */
                    ? <span style={{ fontSize: 11, color: C.muted }}>excluded by role ({r.role})</span>
                    : <button disabled={busy === r.id} onClick={() => toggle(r)}
                        style={{ ...btn, background: r.on_roster ? '#EAF7EE' : '#F3F4F6', color: r.on_roster ? C.green : C.muted,
                          border: '1px solid ' + (r.on_roster ? '#bfe3cb' : '#ddd') }}>
                        {r.on_roster ? '✓ On' : 'Off'}
                      </button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ConfigTab() {
  const [v, setV] = useState(null);
  const [msg, setMsg] = useState('');
  useEffect(() => { api.get('/huddle/config').then(r => setV(r.data.data)).catch(() => {}); }, []);
  if (!v) return <div style={{ color: C.muted }}>Loading…</div>;
  const save = async () => {
    try { await api.put('/huddle/config', { affirmations_text: v.affirmations_text }); setMsg('Saved — it will print on the reverse of every sheet.'); }
    catch (e) { setMsg(e.response?.data?.error || 'Failed'); }
  };
  return (
    <div style={{ maxWidth: 720 }}>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>Affirmations</div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>
        Printed on the reverse of every huddle sheet. Paste the passage exactly as it should be read —
        line breaks are kept. Until this is set, the reverse shows a placeholder rather than invented words.
      </div>
      <textarea style={{ ...input, width: '100%', minHeight: 200, lineHeight: 1.7 }} value={v.affirmations_text || ''}
        onChange={e => setV({ ...v, affirmations_text: e.target.value })} placeholder="Paste the affirmations here…" />
      <div style={{ marginTop: 10, display: 'flex', gap: 10, alignItems: 'center' }}>
        <button style={btnP} onClick={save}>Save</button>
        {msg && <span style={{ fontSize: 12, color: C.green }}>{msg}</span>}
      </div>
    </div>
  );
}

// ─── MANAGEMENT DASHBOARD (admin + management) ───────────────────────────────
// Read only. Two actionable panels above the table, then one row per person.
//
// NO SUCCESS PERCENTAGE ANYWHERE, and this is a rule about the screen, not an omission.
// Completed / planned / stuck are three counts. A ratio hides the difference between 12
// planned with 2 stuck and 4 planned with 0 stuck, and once people learn a percentage is
// watched, the safe move is to plan less.
function ManagementDashboard() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    setBusy(true); setErr('');
    api.get('/huddle/management-dashboard')
      .then(r => setD(r.data))
      .catch(e => setErr(e.response?.data?.error || 'Could not load the dashboard'))
      .finally(() => setBusy(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (err) return <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '10px 14px', borderRadius: 6, fontSize: 13 }}>{err}</div>;
  if (!d) return <div style={{ padding: 24, color: C.muted, fontSize: 12.5 }}>Loading…</div>;

  const dt = (v, o) => v ? new Date(v).toLocaleString('en-IN', o || { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : null;
  const tm = (v) => v ? new Date(v).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—';
  const span = `since ${dt(d.span.first_date, { day: '2-digit', month: 'short' })} (${d.span.huddle_days} huddle days)`;

  const th = { padding: '8px 8px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#555',
    textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '2px solid #eee', background: '#F7F6F3', whiteSpace: 'nowrap' };
  const td = { padding: '7px 8px', fontSize: 12.5, borderBottom: '1px solid #f5f5f5', whiteSpace: 'nowrap' };
  const num = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
  const card = { border: '1px solid ' + C.border, borderRadius: 9, padding: 14, marginBottom: 14, background: '#fff' };

  // Attendance is created 'unmarked' and marked later in the morning. Without saying when it
  // was last marked, an all-unmarked screen reads as "nobody came in" instead of "not yet".
  const attNotYet = d.attendance.marked_today === 0;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 12.5, color: C.muted }}>
          {d.day ? <>Today’s huddle is <strong style={{ color: C.text }}>{d.day.status}</strong>. </> : <>No huddle opened today. </>}
          Counts are {span}.
        </div>
        <button onClick={load} disabled={busy} style={{ marginLeft: 'auto', ...btn, border: '1px solid #ddd', fontSize: 12,
          background: C.navy, color: '#fff', opacity: busy ? 0.5 : 1 }}>{busy ? 'Refreshing…' : 'Refresh'}</button>
      </div>

      {/* ── PANEL 1: NOT SUBMITTED, as names. At 07:48 this is everyone; at 10:30 it is the
             list that matters. A count would be readable but not actionable. */}
      <div style={{ ...card, borderLeft: '3px solid ' + (d.not_submitted.length ? C.red : C.green) }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}>
          Not submitted today
        </div>
        {d.not_submitted.length === 0
          ? <div style={{ fontSize: 13, color: C.green, fontWeight: 600 }}>Everyone has submitted.</div>
          : <>
              <div style={{ fontSize: 13, lineHeight: 1.7 }}>
                {d.not_submitted.map((p, i) => (
                  <span key={p.user_id}>
                    <strong>{p.name}</strong>
                    <span style={{ color: C.muted, fontSize: 11 }}> ({p.department || p.role})</span>
                    {i < d.not_submitted.length - 1 ? ', ' : ''}
                  </span>
                ))}
              </div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>
                {d.not_submitted.length} of {d.not_submitted.length + d.submitted_count} · {d.submitted_count} submitted so far
              </div>
            </>}
      </div>

      {/* ── PANEL 2: STUCK ACROSS MORE THAN ONE HUDDLE. Exact carry-key lineage only. */}
      <div style={{ ...card, borderLeft: '3px solid ' + C.amber }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 2 }}>
          Stuck more than one day
        </div>
        <div style={{ fontSize: 10.5, color: C.muted, marginBottom: 8 }}>
          Items carried forward through the huddle, matched by their carry key — an exact lineage, not wording.
        </div>
        {d.stuck_chains.length === 0
          ? <div style={{ fontSize: 13, color: C.muted }}>Nothing carried across two or more huddles.</div>
          : d.stuck_chains.map(c => (
            <div key={c.carry_key} style={{ display: 'flex', gap: 10, alignItems: 'baseline', padding: '5px 0', borderBottom: '1px solid #f7f7f7', fontSize: 12.5 }}>
              <span style={{ fontWeight: 700, color: c.days_stuck >= 4 ? C.red : C.amber, minWidth: 74, fontVariantNumeric: 'tabular-nums' }}>
                {c.days_stuck}d · {c.huddles}×
              </span>
              <span style={{ minWidth: 92, color: C.muted }}>{c.person}</span>
              <span style={{ flex: 1 }}>{c.body}{c.gpo_no && <span style={{ color: C.red, fontSize: 11 }}> · GPO {c.gpo_no}</span>}</span>
            </div>
          ))}
      </div>

      {/* ── ADVISORY. Structurally and visually separate from the panel above, phrased as a
             question, never merged into the chain counts, and never affecting the sort. */}
      {d.advisory.length > 0 && (
        <div style={{ border: '1px dashed #c9c7e8', background: '#F7F7FC', borderRadius: 9, padding: 14, marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#4A489B', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 2 }}>
            Possibly the same item — worth a look
          </div>
          <div style={{ fontSize: 10.5, color: '#6b6a9c', marginBottom: 8 }}>
            Typed fresh today, so they carry no lineage and are <em>not</em> counted as stuck above.
          </div>
          {d.advisory.map(a => (
            <div key={a.id} style={{ fontSize: 12.5, padding: '5px 0', borderBottom: '1px solid #ecebf6' }}>
              <span style={{ color: C.muted, minWidth: 92, display: 'inline-block' }}>{a.person}</span>
              <strong>{a.body}</strong>
              <div style={{ fontSize: 11, color: '#6b6a9c', marginTop: 2 }}>
                Typed fresh, but similar wording appeared on {a.earlier_days} earlier day{a.earlier_days === 1 ? '' : 's'} — possibly the same item?
                <span style={{ color: C.muted }}> Closest: “{a.closest_earlier}”</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── ATTENDANCE FRESHNESS, stated before the table so the P/A/L/E column is readable. */}
      <div style={{ fontSize: 11.5, color: attNotYet ? C.amber : C.muted, marginBottom: 8 }}>
        {attNotYet
          ? <>Attendance <strong>has not been marked yet today</strong> — the roster starts unmarked and the facilitator marks it during the huddle.
              {d.attendance.last_marked_day && <> Last marked on {dt(d.attendance.last_marked_day, { day: '2-digit', month: 'short' })}
                {d.attendance.last_marked_day_at ? ` at ${tm(d.attendance.last_marked_day_at)}` : ''}.</>}</>
          : <>Attendance marked for {d.attendance.marked_today} of {d.attendance.roster_today}
              {d.attendance.last_marked_at ? <> · last marked {dt(d.attendance.last_marked_at)}</> : null}</>}
      </div>

      <div style={{ overflowX: 'auto', border: '1px solid ' + C.border, borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th} rowSpan={2}>Person</th>
              <th style={{ ...th, textAlign: 'center' }} colSpan={2}>Attendance</th>
              <th style={{ ...th, textAlign: 'center' }} colSpan={2}>Today</th>
              <th style={{ ...th, textAlign: 'center' }} colSpan={3}>This week</th>
              <th style={{ ...th, textAlign: 'right' }} rowSpan={2}>Done<br />{span.replace('since ', '')}</th>
              <th style={{ ...th, textAlign: 'center' }} colSpan={2}>Stuck</th>
              <th style={th} rowSpan={2}>Submitted</th>
            </tr>
            <tr>
              <th style={th}>today</th>
              <th style={{ ...th, textAlign: 'right' }}>P/A/L/E</th>
              <th style={{ ...th, textAlign: 'right' }}>planned</th>
              <th style={{ ...th, textAlign: 'right' }}>done</th>
              <th style={{ ...th, textAlign: 'right' }}>planned</th>
              <th style={{ ...th, textAlign: 'right' }}>done</th>
              <th style={{ ...th, textAlign: 'right' }}>stuck</th>
              <th style={{ ...th, textAlign: 'right' }}>items</th>
              <th style={{ ...th, textAlign: 'right' }}>oldest</th>
            </tr>
          </thead>
          <tbody>
            {d.rows.map(r => {
              const stuckWeek = Math.max(0, Number(r.planned_week) - Number(r.done_week));
              const A = { present: ['P', C.green], absent: ['A', C.red], late: ['L', C.amber],
                          excused: ['E', C.navy], unmarked: ['·', C.muted] }[r.attendance] || ['·', C.muted];
              return (
                <tr key={r.user_id} style={{ borderLeft: '3px solid ' + (r.stuck_items > 0 ? C.amber : 'transparent') }}>
                  <td style={td}>
                    <strong>{r.name}</strong>
                    {/* Two active users are both named "Ravi Kumar" — the role is what tells
                        them apart on screen, and the row is keyed on user_id regardless. */}
                    <div style={{ fontSize: 10, color: C.muted }}>{r.department || r.role}</div>
                  </td>
                  <td style={{ ...td, textAlign: 'center', fontWeight: 700, color: A[1] }}>{A[0]}</td>
                  <td style={{ ...num, fontSize: 11.5, color: C.muted }}>
                    {r.p_cnt}/{r.a_cnt}/<span title="'late' has never been recorded — the column is kept because its emptiness is itself information">{r.l_cnt}</span>/{r.e_cnt}
                  </td>
                  <td style={num}>{r.planned_today}</td>
                  <td style={{ ...num, color: r.done_today > 0 ? C.green : C.muted }}>{r.done_today}</td>
                  <td style={num}>{r.planned_week}</td>
                  <td style={{ ...num, color: r.done_week > 0 ? C.green : C.muted }}>{r.done_week}</td>
                  <td style={{ ...num, color: stuckWeek > 0 ? C.amber : C.muted }}>{stuckWeek}</td>
                  <td style={{ ...num, fontWeight: 600 }}>{r.done_cum}</td>
                  <td style={{ ...num, fontWeight: r.stuck_items > 0 ? 700 : 400, color: r.stuck_items > 0 ? C.amber : C.muted }}>{r.stuck_items}</td>
                  <td style={{ ...num, color: r.oldest_days >= 4 ? C.red : C.muted }}>
                    {r.oldest_days != null ? `${r.oldest_days}d` : '—'}
                  </td>
                  <td style={{ ...td, color: r.submitted_at ? C.green : C.red, fontWeight: r.submitted_at ? 400 : 700 }}>
                    {r.submitted_at ? tm(r.submitted_at) : 'not yet'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ fontSize: 10.5, color: C.muted, marginTop: 10, lineHeight: 1.6, maxWidth: 860 }}>
        Sorted by stuck items, then by who has not submitted — the people who need attention come to the top rather than being found.
        Stuck counts <strong>distinct carried items</strong>, not rows: some entries hold the same item twice, and counting rows
        would put someone at the top because of a duplicate. There is deliberately no success percentage — completed, planned and
        stuck are three separate counts.
      </div>
    </div>
  );
}
